/**
 * PDP 'complementary-quick-add' block ("Complete o look").
 * <pdp-quick-add-item> maps the custom-select values of one card to a variant; its checkbox
 * puts the card in or out of the bundle;
 * <pdp-quick-add-bundle> inserts the current product card, keeps the button label and total
 * label up to date and adds every checked card to the cart in one request.
 */
if (!customElements.get('pdp-quick-add-item')) {
  /**
   * Product pop-up opened by clicking a card's image or title: images, colour/size selects and
   * description, no buy button. The markup comes from /products/handle?view=popup
   * (templates/product.popup.liquid) and picking a colour or size mirrors it on the card.
   */
  class PdpQuickAddPopup {
    static cache = new Map();

    static modal = null;

    /**
     * The pop-up shell, created from the block's <template> on first use and moved to <body>.
     * @returns {HTMLElement}
     */
    static getModal() {
      if (!PdpQuickAddPopup.modal) {
        const template = document.querySelector('template.pdp-quick-add__popup-template');
        PdpQuickAddPopup.modal = template.content.firstElementChild.cloneNode(true);
        document.body.append(PdpQuickAddPopup.modal);
      }
      return PdpQuickAddPopup.modal;
    }

    /**
     * Fetches (once per product) and opens the pop-up. Falls back to the product page if it fails.
     * @param {PdpQuickAddItem} item - Card that was clicked.
     * @param {HTMLAnchorElement} opener - The image or title link that was clicked.
     */
    static async open(item, opener) {
      const url = new URL(opener.href);
      url.searchParams.set('view', 'popup');

      let html = PdpQuickAddPopup.cache.get(url.href);
      if (!html) {
        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
          html = await response.text();
          PdpQuickAddPopup.cache.set(url.href, html);
        } catch (error) {
          window.location.href = opener.href;
          return;
        }
      }

      const modal = PdpQuickAddPopup.getModal();
      modal.querySelector('.modal__content').innerHTML = html;
      const root = modal.querySelector('.pdp-popup');
      modal.setAttribute('aria-label', root.querySelector('.pdp-popup__title').textContent.trim());
      modal.querySelector('.pdp-popup__full').href = opener.href;
      PdpQuickAddPopup.bind(root, item);
      modal.open(opener);
    }

    /**
     * Shows the card's colour and size, keeps the card in step with the pop-up's selects and
     * scrolls the images to the chosen colour.
     * @param {HTMLElement} root - The .pdp-popup element.
     * @param {PdpQuickAddItem} item - Card to mirror.
     */
    static bind(root, item) {
      const variants = JSON.parse(root.querySelector('script[type="application/json"]').textContent);
      const selects = [...root.querySelectorAll('custom-select')];
      const slides = root.querySelector('.pdp-popup__slides');

      const show = (mediaId) => {
        const slide = slides.querySelector(`[data-media-id="${mediaId}"]`);
        if (slide) slides.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      };

      // Card -> pop-up: start on the card's selection
      item.getSelectedValues().forEach((value, index) => {
        const select = selects[index];
        const option = select && [...select.querySelectorAll('.js-option')].find((el) => el.dataset.value === value);
        if (option) select.selectOption(option);
      });

      // Pop-up -> card (bound after the initial sync above, which fires 'change' too)
      root.addEventListener('change', () => {
        const values = selects.map((select) => select.querySelector('[aria-selected="true"]')?.dataset.value || '');
        item.selectValues(values);
        const variant = variants.find((v) => values.every((value, index) => !value || v.options[index] === value));
        if (variant?.mediaId) show(variant.mediaId);
      });

      // Drag the images with the mouse like a carousel (touch already scrolls natively)
      let drag = null;
      const endDrag = () => {
        if (!drag) return;
        drag = null;
        slides.classList.remove('is-dragging');
        slides.scrollTo({ left: Math.round(slides.scrollLeft / slides.clientWidth) * slides.clientWidth, behavior: 'smooth' });
      };
      slides.addEventListener('pointerdown', (evt) => {
        if (evt.pointerType !== 'mouse' || evt.button !== 0) return;
        drag = { x: evt.clientX, left: slides.scrollLeft };
        slides.classList.add('is-dragging');
        slides.setPointerCapture(evt.pointerId);
      });
      slides.addEventListener('pointermove', (evt) => {
        if (drag) slides.scrollLeft = drag.left - (evt.clientX - drag.x);
      });
      slides.addEventListener('pointerup', endDrag);
      slides.addEventListener('pointercancel', endDrag);

      root.querySelectorAll('.pdp-popup__thumb').forEach((thumb) => {
        thumb.addEventListener('click', () => show(thumb.dataset.mediaId));
      });

      // Open on the card's colour image
      const current = variants.find((v) => item.getSelectedValues().every((value, index) => !value || v.options[index] === value));
      slides.scrollTo({ left: 0 });
      if (current?.mediaId) {
        const slide = slides.querySelector(`[data-media-id="${current.mediaId}"]`);
        if (slide) slides.scrollTo({ left: slide.offsetLeft });
      }
    }
  }

  class PdpQuickAddItem extends HTMLElement {
    connectedCallback() {
      if (this.variants) return;

      this.variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      this.selects = [...this.querySelectorAll('custom-select')];
      this.checkbox = this.querySelector('.pdp-quick-add__include');
      this.price = this.querySelector('.pdp-quick-add__price');
      this.error = this.querySelector('.pdp-quick-add__error');
      this.image = this.querySelector('.pdp-quick-add__img');
      this.defaultSrcset = this.image?.srcset;

      // Option selects and the checkbox both fire 'change'
      this.addEventListener('change', this.update.bind(this));
      this.addEventListener('click', this.handleLinkClick.bind(this));
      this.update();
    }

    /**
     * Opens the product pop-up from the card's image or title (plain left click only, so
     * "open in new tab" and the like keep going to the product page).
     * @param {MouseEvent} evt - Click event.
     */
    handleLinkClick(evt) {
      const link = evt.target.closest('a.pdp-quick-add__media, a.pdp-quick-add__title');
      if (!link || evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
      evt.preventDefault();
      PdpQuickAddPopup.open(this, link);
    }

    /**
     * Whether this card goes in the cart: checked and with an available variant.
     * @returns {boolean}
     */
    get included() {
      return this.checkbox.checked && Boolean(this.variant?.available);
    }

    /**
     * Selected value of each option ('' when only the placeholder is selected).
     * @returns {string[]}
     */
    getSelectedValues() {
      return this.selects.map((select) => select.querySelector('[aria-selected="true"]')?.dataset.value || '');
    }

    /**
     * Selects the given option values (used to follow the main variant picker).
     * @param {string[]} values - Option values, in option order.
     */
    selectValues(values) {
      values.forEach((value, index) => {
        const select = this.selects[index];
        const option = select && [...select.querySelectorAll('.js-option')].find((el) => el.dataset.value === value);
        if (option) select.selectOption(option);
      });
    }

    /**
     * Whether a variant matches the selected values, ignoring empty ones.
     * @param {object} variant - Variant data.
     * @param {string[]} values - Selected values.
     * @param {number} [skipIndex] - Option index to ignore.
     * @returns {boolean}
     */
    static matches(variant, values, skipIndex = -1) {
      return values.every((value, index) => index === skipIndex || !value || variant.options[index] === value);
    }

    update() {
      const values = this.getSelectedValues();

      // Flag values that have no stock with the other current selections.
      this.selects.forEach((select, index) => {
        select.querySelectorAll('.js-option[data-value-id]').forEach((option) => {
          const available = this.variants.some((variant) => variant.available
            && variant.options[index] === option.dataset.value
            && PdpQuickAddItem.matches(variant, values, index));
          option.classList.toggle('is-unavailable', !available);
          option.querySelector('em')?.remove();
          if (!available) {
            const label = document.createElement('em');
            label.className = 'pointer-events-none';
            label.textContent = this.dataset.noStockText;
            option.append(label);
          }
        });
      });

      // Show the image of the chosen colour.
      if (this.image) {
        const withImage = values.some(Boolean)
          && this.variants.find((variant) => variant.image && PdpQuickAddItem.matches(variant, values));
        this.image.srcset = withImage ? withImage.image : this.defaultSrcset;
      }

      this.variant = values.every(Boolean)
        ? this.variants.find((v) => PdpQuickAddItem.matches(v, values))
        : null;
      if (this.variant) this.price.innerHTML = this.variant.priceHtml;

      // Sold out: the card can't be included until another option is chosen
      const noStock = Boolean(this.variant && !this.variant.available);
      this.checkbox.disabled = noStock;
      this.error.hidden = !noStock;
      this.error.textContent = noStock ? this.dataset.noStockText : '';
      this.classList.toggle('is-off', !this.included);

      this.dispatchEvent(new CustomEvent('pdp-quick-add:change', { bubbles: true }));
    }
  }

  customElements.define('pdp-quick-add-item', PdpQuickAddItem);
}

if (!customElements.get('pdp-quick-add-bundle')) {
  class PdpQuickAddBundle extends HTMLElement {
    connectedCallback() {
      if (this.list) return;

      this.list = this.querySelector('.pdp-quick-add__list');
      this.total = this.querySelector('.pdp-quick-add__total');
      this.count = this.querySelector('.pdp-quick-add__count');
      this.button = this.querySelector('.pdp-quick-add__submit');
      this.buttonPrice = this.querySelector('.pdp-quick-add__cta-mobile');
      this.error = this.querySelector('.pdp-quick-add__footer-error');
      this.money = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'BRL'
      });

      this.addEventListener('pdp-quick-add:change', this.updateTotal.bind(this));
      this.button.addEventListener('click', this.addToCart.bind(this));

      // The current product card is rendered on the page, outside the fetched markup.
      const template = this.closest('.pdp-quick-add')?.querySelector('template.pdp-quick-add__current');
      if (template) this.list.prepend(template.content.cloneNode(true));
      this.current = this.list.querySelector('pdp-quick-add-item[data-current]');

      this.section = this.closest('.shopify-section');
      this.onVariantChange = this.handleVariantChange.bind(this);
      document.addEventListener('on:variant:change', this.onVariantChange);

      this.updateTotal();
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.onVariantChange);
    }

    /**
     * Cards whose element has been upgraded.
     * @returns {Element[]}
     */
    get items() {
      return [...this.querySelectorAll('pdp-quick-add-item')].filter((item) => item.variants);
    }

    /**
     * Keeps the current product card on the variant chosen in the main picker.
     * @param {CustomEvent} evt - 'on:variant:change' event.
     */
    handleVariantChange(evt) {
      if (!this.current || this.contains(evt.target) || !this.section?.contains(evt.target)) return;
      const { variant } = evt.detail || {};
      if (variant?.options) this.current.selectValues(variant.options);
    }

    /**
     * Text under the total (desktop), by number of checked items.
     * @param {number} count - Checked items.
     * @returns {string}
     */
    static countLabel(count) {
      if (count === 0) return 'Selecione ao menos 1 peça';
      if (count === 1) return 'Adicionar a peça ao carrinho';
      return `Adicionar os ${count} ao carrinho`;
    }

    /**
     * Mobile button text up to the price, by number of checked items.
     * @param {number} count - Checked items.
     * @returns {string}
     */
    static buttonLabel(count) {
      if (count === 0) return 'Selecione ao menos 1 peça';
      if (count === 1) return 'Comprar a peça: ';
      return `Comprar os ${count}: `;
    }

    /**
     * Desktop: "Preço total" and the count text sit above a plain "Adicionar" button.
     * Mobile: the button itself reads "Comprar os N: R$ total".
     */
    updateTotal() {
      const included = this.items.filter((item) => item.included);
      const cents = included.reduce((sum, item) => sum + item.variant.price, 0);
      const nodes = [PdpQuickAddBundle.buttonLabel(included.length)];
      if (included.length) {
        for (const part of this.money.formatToParts(cents / 100)) {
          if (part.type === 'currency') {
            const sup = document.createElement('span');
            sup.className = 'pdp-quick-add__currency';
            sup.textContent = part.value;
            nodes.push(sup);
          } else if (part.value.trim()) {
            nodes.push(part.value);
          }
        }
      }
      this.total.textContent = this.money.format(cents / 100);
      this.count.textContent = PdpQuickAddBundle.countLabel(included.length);
      this.buttonPrice.replaceChildren(...nodes);
      this.button.disabled = included.length === 0;
    }

    async addToCart() {
      const items = this.items
        .filter((item) => item.included)
        .map((item) => ({ id: item.variant.id, quantity: 1 }));
      if (!items.length || this.button.getAttribute('aria-disabled') === 'true') return;

      // Same section refresh as ProductForm (main.js), so the header count and drawer update.
      const sectionsToUpdate = ['page-header', 'cart-drawer'].map((sel) => document.querySelector(sel)).filter((el) => el);
      const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);

      this.error.hidden = true;
      this.button.setAttribute('aria-disabled', 'true');
      this.button.classList.add('is-loading');

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || response.status);

        if (theme.settings.afterAddToCart === 'page') {
          window.location.href = theme.routes.cart;
          return;
        }

        if (data.sections && sectionsToUpdate.every((el) => data.sections[el.dataset.sectionId])) {
          sectionsToUpdate.forEach((el) => el.updateFromCartChange(data.sections[el.dataset.sectionId]));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        }

        if (theme.settings.afterAddToCart === 'drawer') {
          document.querySelector('.js-cart-drawer')?.open(this.button);
        }

        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantId: items[0].id }
        }));
      } catch (error) {
        this.error.textContent = error.message;
        this.error.hidden = false;
        // Cart may change despite error
        this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      } finally {
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-disabled');
      }
    }
  }

  customElements.define('pdp-quick-add-bundle', PdpQuickAddBundle);
}
